// Единая точка HTTP-запросов к backend. initData из Telegram передаётся в каждом запросе —
// backend валидирует её на каждый вызов (ТЗ раздел 4.5), клиент сам ничего не проверяет и не хранит
// секретов (принцип "единой точки правды", ТЗ раздел 2.2).

import WebApp from "@twa-dev/sdk";

import { extractErrorMessage } from "./errorMessage";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-Telegram-Init-Data": WebApp.initData,
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new ApiError(response.status, extractErrorMessage(body, response.statusText));
  }

  // 204 No Content (DELETE-эндпоинты) — тела нет, response.json() упал бы на пустой строке.
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  delete: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
};

// Casing и состав полей — как у backend Pydantic-схем (app/schemas/*.py), snake_case, не
// camelCase: FastAPI/Pydantic здесь ничего не транслитерирует, а вводить отдельный слой
// маппинга ради одной лишь смены регистра букв — накладные расходы без пользы (см. docs/DECISIONS.md).

export interface Plan {
  id: number;
  code: string;
  title: string;
  price: number;
  currency: string;
  duration_days: number;
  // Максимум покупаемого количества устройств для этого тарифа (1-5) — не лимит конкретной
  // подписки, см. Subscription.device_limit.
  device_limit: number;
  is_trial: boolean;
  // "Расширенный" — покупка создаёт две подписки разных тарифных групп AV-Desk, апгрейд на/с
  // такого тарифа не поддерживается (см. app/api/routes/miniapp/change_plan.py).
  is_bundle: boolean;
}

export type SubscriptionStatus = "active" | "expiring" | "expired" | "cancelled";

export interface Device {
  id: number;
  name: string;
  os: string | null;
  attached_at: string;
  last_seen_at: string | null;
  is_primary: boolean;
  // Сетевой статус на AV-Desk (2026-08-20, app/services/device_status.py) — синхронизируется при
  // каждом открытии экрана "Моя подписка" + фоново раз в 10 минут.
  is_online: boolean;
}

// Урезанная версия Subscription (без своего bundle_partner) — только для отображения "второй
// половины" пары "Расширенный" внутри Subscription.bundle_partner ниже. Совпадает с
// SubscriptionSummaryOut на backend (app/schemas/subscription.py) — там же объяснение, почему без
// этого backend ушёл бы в рекурсию при сериализации (обе половины ссылаются друг на друга).
export interface SubscriptionSummary {
  id: number;
  status: SubscriptionStatus;
  plan: Plan;
  started_at: string;
  expires_at: string;
  auto_renew: boolean;
  devices: Device[];
  device_limit: number;
}

export interface Subscription {
  id: number;
  status: SubscriptionStatus;
  plan: Plan;
  started_at: string;
  expires_at: string;
  auto_renew: boolean;
  devices: Device[];
  // Реально купленное количество устройств (1-5) — не plan.device_limit, тот теперь означает
  // "максимум покупаемого количества для тарифа", а не лимит конкретной подписки.
  device_limit: number;
  // Вторая половина пары "Расширенный" (Plan.is_bundle покупка создаёт ДВЕ Subscription, см.
  // app/services/purchases.py::activate_bundle_subscription) — null у всех обычных подписок.
  // Решает "несколько активных подписок на пользователя" (docs/OPEN_QUESTIONS.md, 2026-08-20).
  bundle_partner: SubscriptionSummary | null;
}

export interface CheckoutResponse {
  invoice_link: string;
  amount_due: number;
}

export interface InstallPackage {
  os: string;
  url: string;
}

export interface AttachDeviceResponse {
  device: Device;
  install_packages: InstallPackage[];
}

export type PaymentStatus = "pending" | "succeeded" | "failed" | "refunded";
export type PaymentType = "purchase" | "renewal" | "upgrade" | "refund";
export type PaymentProvider = "telegram" | "acquiring";

export interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: PaymentStatus;
  type: PaymentType;
  provider: PaymentProvider;
  created_at: string;
}

export const fetchPlans = () => api.get<Plan[]>("/miniapp/plans");
// "Текущая" подписка пользователя — нужна только ChangePlanScreen.tsx (апгрейд по-прежнему
// применяется к самой свежей подписке, см. docs/OPEN_QUESTIONS.md). Экран "Моя подписка" больше
// её не использует — см. fetchAllSubscriptions ниже.
export const fetchSubscription = () => api.get<Subscription | null>("/miniapp/subscription");
// ВСЕ активные подписки пользователя, каждая полноценно управляема отдельно (2026-08-20, запрос
// пользователя: "нужно выполнить полноценное управление каждым экземпляром отдельно") — основной
// источник данных экрана "Моя подписка" (MySubscriptionScreen.tsx группирует по тарифу сам).
// Вторые половины пары "Расширенный" backend уже исключает (см.
// app/api/routes/miniapp/subscriptions.py), но у каждого элемента есть свой bundle_partner.
export const fetchAllSubscriptions = () => api.get<Subscription[]>("/miniapp/subscriptions");
export const checkout = (planId: number, quantity: number) =>
  api.post<CheckoutResponse>("/miniapp/checkout", { plan_id: planId, quantity });
// subscriptionId — явный (2026-08-20, было неявно "текущая подписка пользователя"), см.
// app/schemas/device.py::AttachDeviceRequest.
export const attachDevice = (name: string, subscriptionId: number) =>
  api.post<AttachDeviceResponse>("/miniapp/devices", { name, subscription_id: subscriptionId });
export const fetchDeviceInstallPackages = (deviceId: number) =>
  api.get<InstallPackage[]>(`/miniapp/devices/${deviceId}/install-packages`);
export const detachDevice = (deviceId: number) => api.delete(`/miniapp/devices/${deviceId}`);
export const fetchPayments = () => api.get<Payment[]>("/miniapp/payments");

export interface RedeemPromoResponse {
  subscription: Subscription;
  install_packages: InstallPackage[];
}

export const redeemPromo = (code: string) =>
  api.post<RedeemPromoResponse>("/miniapp/promo", { code });
// subscriptionId в пути (2026-08-20, было неявно "текущая подписка пользователя") — полноценное
// управление каждым экземпляром отдельно, см. app/api/routes/miniapp/subscriptions.py.
export const cancelAutoRenew = (subscriptionId: number) =>
  api.post<Subscription>(`/miniapp/subscription/${subscriptionId}/cancel-auto-renew`);
export const resumeAutoRenew = (subscriptionId: number) =>
  api.post<Subscription>(`/miniapp/subscription/${subscriptionId}/resume-auto-renew`);

export interface ChangePlanResponse {
  invoice_link: string;
  amount_due: number;
}

// subscriptionId — явный (2026-08-20, было неявно "текущая подписка пользователя") — раздел
// "Управление" показывает ВСЕ активные подписки, апгрейд применяется к конкретной выбранной.
export const changePlan = (newPlanId: number, subscriptionId: number) =>
  api.post<ChangePlanResponse>("/miniapp/change-plan", {
    new_plan_id: newPlanId,
    subscription_id: subscriptionId,
  });
